import { type ResultOf } from '@graphql-typed-document-node/core'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { type TimeSeriesSettingsPage_MainQuery as Data } from '~/gql/graphql'
import {
  ensureExists,
  exists,
  filterExists,
  findByPath,
  pathAssume,
  SurveyOp,
} from '~/utils'

import { useMainQueryCustomerAttributeOrderTable } from './useMainQueryCustomerAttributeOrderTable'
import { useMainQueryKey } from './useMainQueryKey'

const surveyFragment = graphql(`
  fragment TimeSeriesSettingsPage_UseMainQueryData_Survey on Survey {
    metadata {
      surveyStartDate {
        year
        month
        day
      }
    }
  }
`)

const processSurveys = <TSurvey extends ResultOf<typeof surveyFragment>>(
  surveys: (TSurvey | null)[] | null | undefined,
  message: string,
) =>
  R.pipe(
    surveys,
    filterExists,
    R.map((survey) => ({
      ...survey,
      metadata: ensureExists(survey.metadata, message),
    })),
    R.sort(SurveyOp.compareByStartDate),
  )

export const useMainQueryData = () => {
  const { t } = useTranslation()
  const queryKey = useMainQueryKey()

  const { data } = useQuery<Data>({ enabled: false, queryKey })

  const customerAttributeOrderTable = useMainQueryCustomerAttributeOrderTable()

  return React.useMemo(() => {
    const brandAccount = data?.brandAccount

    if (!exists(brandAccount)) {
      return null
    }

    return R.pipe(brandAccount, (brandAccount) => ({
      ...brandAccount,
      surveyGroups: R.pipe(
        brandAccount.surveyGroups,
        filterExists,
        R.map((surveyGroup) => ({
          ...surveyGroup,
          alertSurveys: surveyGroup.alertSurveys ?? false,
          surveys: processSurveys(surveyGroup.surveys, t('assertion_failed')),
          brands: R.pipe(
            surveyGroup.brands,
            filterExists,
            R.map((brand) => ({
              ...brand,
              attachedBrands: filterExists(brand.attachedBrands),
            })),
          ),
          questions: R.pipe(
            surveyGroup.questions,
            filterExists,
            R.map((question) => ({
              ...question,
              attachedQuestions: filterExists(question.attachedQuestions),
              options: R.pipe(
                question.options,
                filterExists,
                R.map((option) => ({
                  ...option,
                  attachedOptions: filterExists(option.attachedOptions),
                })),
              ),
            })),
          ),
          customerAttributeGroups: R.pipe(
            surveyGroup.customerAttributeGroups,
            filterExists,
            R.map((customerAttributeGroup) => ({
              ...customerAttributeGroup,
              attachedCustomerAttributeGroups: filterExists(
                customerAttributeGroup.attachedCustomerAttributeGroups,
              ),
              customerAttributes: R.pipe(
                customerAttributeGroup.customerAttributes,
                filterExists,
                R.filter((customerAttribute) => exists(customerAttribute.id)),
                R.map((customerAttribute) => ({
                  ...customerAttribute,
                  id: ensureExists(customerAttribute.id, t('assertion_failed')),
                  attachedCustomerAttributes: R.pipe(
                    customerAttribute.attachedCustomerAttributes,
                    filterExists,
                    R.map((attachedCustomerAttribute) => ({
                      surveyId: attachedCustomerAttribute.surveyId,
                      order: R.pipe(
                        customerAttributeOrderTable,
                        findByPath(
                          'customerAttributeId',
                          attachedCustomerAttribute.customerAttributeId as string,
                        ),
                        pathAssume('order'),
                      ),
                    })),
                  ),
                })),
              ),
            })),
          ),
        })),
      ),
      surveys: R.pipe(
        processSurveys(brandAccount.surveys, t('assertion_failed')),
        R.map((survey) => ({
          ...survey,
          brandGroups: R.pipe(
            survey.brandGroups,
            filterExists,
            R.map((brandGroup) => ({
              ...brandGroup,
              brands: filterExists(brandGroup.brands),
            })),
          ),
          questions: filterExists(survey.questions),
          customerAttributeGroups: R.pipe(
            survey.customerAttributeGroups,
            filterExists,
            R.map((customerAttributeGroup) => ({
              ...customerAttributeGroup,
              customerAttributes: R.pipe(
                customerAttributeGroup.customerAttributes,
                filterExists,
                R.map((customerAttribute) => ({
                  order: R.pipe(
                    customerAttributeOrderTable,
                    findByPath('customerAttributeId', customerAttribute.id),
                    pathAssume('order'),
                  ),
                  name: customerAttribute.name,
                })),
              ),
            })),
          ),
        })),
      ),
    }))
  }, [customerAttributeOrderTable, data, t])
}

type BrandAccount = NonNullable<ReturnType<typeof useMainQueryData>>
export type SurveyGroup = BrandAccount['surveyGroups'][number]
export type SurveyGroupSurvey = SurveyGroup['surveys'][number]
